import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { CalendarDays, Flame, Beef, Scale, Syringe, TrendingUp, TrendingDown, Minus, Sparkles, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

const TEAL = "#0D9488";

function MiniStat({ Icon, label, value, sub, tint = "text-slate-900", testid }) {
  return (
    <div
      className="flex-1 min-w-[130px] border border-slate-200 rounded-lg bg-white px-3 py-2.5"
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        <Icon className="w-3 h-3" style={{ color: TEAL }} /> {label}
      </div>
      <div className={`font-semibold text-lg tabular-nums leading-tight mt-1 ${tint}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 tabular-nums mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * "This week at a glance" — pulls from GET /insights/weekly and shows 4 tiles:
 *  1. Nutrition goals hit (calories + protein)
 *  2. Weight delta over the window
 *  3. Top peptide by shot count
 *  4. Weight-log streak
 *
 * Silently renders nothing until data loads to avoid flashing an empty card.
 */
export default function WeeklyInsightsCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/insights/weekly");
      setData(data);
    } catch { setError(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error || !data) return null;

  const weight = data.weight || {};
  const wDelta = weight.delta;
  const wUnit = weight.unit || "lb";
  const wHasData = weight.count > 0 && wDelta != null;
  const wUp = wHasData && wDelta > 0;
  const wDown = wHasData && wDelta < 0;
  const WIcon = wUp ? TrendingUp : wDown ? TrendingDown : Minus;
  const wTint = wUp ? "text-amber-700" : wDown ? "text-emerald-700" : "text-slate-900";

  const proteinRate = data.protein_hit_rate ?? 0;
  const proteinDays = data.protein_hit_days ?? 0;
  const calDays = data.calorie_hit_days ?? 0;
  const calGoal = data.calorie_goal;
  const nDays = data.nutrition_days ?? 0;

  const top = data.top_peptide;
  const streak = data.streak ?? 0;

  return (
    <Card
      className="border border-teal-200 bg-gradient-to-br from-teal-50/60 via-white to-white rounded-xl p-5"
      data-testid="weekly-insights-card"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-teal-50"
            style={{ color: TEAL }}
          >
            <CalendarDays className="w-4 h-4" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>
              This Week at a Glance
            </div>
            <div className="text-sm text-slate-700 mt-0.5">Past 7 days · {data.shots || 0} shot{data.shots === 1 ? "" : "s"} · {nDays} day{nDays === 1 ? "" : "s"} of nutrition logged</div>
          </div>
        </div>
        <Link
          to="/history"
          className="text-[11px] font-semibold uppercase tracking-widest hover:underline"
          style={{ color: TEAL }}
          data-testid="weekly-insights-history-link"
        >
          Full history →
        </Link>
      </div>

      <div className="flex flex-wrap gap-2.5">
        {/* Nutrition — calories + protein hit-rate */}
        <MiniStat
          testid="weekly-insights-nutrition"
          Icon={Flame}
          label="Nutrition"
          value={
            calGoal
              ? <span>{calDays}<span className="text-slate-400 font-normal text-sm">/7</span> <span className="text-slate-500 text-xs">cal hits</span></span>
              : <span className="text-slate-500 text-sm">Set a calorie goal</span>
          }
          sub={proteinRate > 0 ? `${proteinDays}/7 protein · ${proteinRate}% streak` : "Log meals to unlock streaks"}
        />

        {/* Weight delta */}
        <MiniStat
          testid="weekly-insights-weight"
          Icon={WIcon}
          label="Weight"
          value={
            wHasData
              ? <span className={wTint}>{wDelta > 0 ? "+" : ""}{wDelta} <span className="text-slate-500 text-sm font-normal">{wUnit}</span></span>
              : <span className="text-slate-500 text-sm">No logs yet</span>
          }
          sub={wHasData ? `${weight.first} → ${weight.last} ${wUnit} · ${weight.count} log${weight.count === 1 ? "" : "s"}` : "Log two weights to see the delta"}
        />

        {/* Top peptide */}
        <MiniStat
          testid="weekly-insights-top-peptide"
          Icon={Syringe}
          label="Top Peptide"
          value={top ? <span className="truncate block max-w-[160px]" title={top.name}>{top.name}</span> : <span className="text-slate-500 text-sm">No shots logged</span>}
          sub={top ? `${top.count} shot${top.count === 1 ? "" : "s"} · ${top.total_dose}${top.unit}` : "Log a shot to see your leader"}
        />

        {/* Streak */}
        <MiniStat
          testid="weekly-insights-streak"
          Icon={Trophy}
          label="Weigh-In Streak"
          value={
            streak > 0
              ? <span>{streak}<span className="text-slate-500 text-sm font-normal ml-1">day{streak === 1 ? "" : "s"}</span></span>
              : <span className="text-slate-500 text-sm">Weigh in to start</span>
          }
          sub={streak >= 7 ? "🔥 A full week — keep it going" : streak > 0 ? "Consecutive weigh-ins" : "One weigh-in a day builds it"}
        />
      </div>

      {/* Nudge only when the user has no data at all */}
      {(!nDays && !top && !wHasData) && (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <Sparkles className="w-3.5 h-3.5" style={{ color: TEAL }} />
          Once you log a few shots, meals or weights, this card lights up with your week's momentum.
        </div>
      )}
    </Card>
  );
}
